/**
 * AsciiRenderer.ts - Main ASCII rendering class for DEFCON TERMINAL
 *
 * Provides core functionality for rendering ASCII art to terminal,
 * including frame-by-frame animations, centered rendering, box drawing,
 * and text overlays.
 */

export interface RenderOptions {
  centered?: boolean;
  terminalWidth?: number;
  terminalHeight?: number;
  color?: string;
  bold?: boolean;
  dim?: boolean;
  blink?: boolean;
}

export interface BoxOptions {
  width: number;
  height: number;
  title?: string;
  style?: 'single' | 'double' | 'heavy' | 'rounded';
  padding?: number;
}

export interface TextOverlay {
  text: string;
  x: number;
  y: number;
  color?: string;
}

// Box drawing character sets
const BOX_STYLES = {
  single: {
    topLeft: '┌',
    topRight: '┐',
    bottomLeft: '└',
    bottomRight: '┘',
    horizontal: '─',
    vertical: '│',
    leftT: '├',
    rightT: '┤',
    topT: '┬',
    bottomT: '┴',
    cross: '┼'
  },
  double: {
    topLeft: '╔',
    topRight: '╗',
    bottomLeft: '╚',
    bottomRight: '╝',
    horizontal: '═',
    vertical: '║',
    leftT: '╠',
    rightT: '╣',
    topT: '╦',
    bottomT: '╩',
    cross: '╬'
  },
  heavy: {
    topLeft: '┏',
    topRight: '┓',
    bottomLeft: '┗',
    bottomRight: '┛',
    horizontal: '━',
    vertical: '┃',
    leftT: '┣',
    rightT: '┫',
    topT: '┳',
    bottomT: '┻',
    cross: '╋'
  },
  rounded: {
    topLeft: '╭',
    topRight: '╮',
    bottomLeft: '╰',
    bottomRight: '╯',
    horizontal: '─',
    vertical: '│',
    leftT: '├',
    rightT: '┤',
    topT: '┬',
    bottomT: '┴',
    cross: '┼'
  }
};

// Terminal color codes using the engine's \c[color] format instead of raw ANSI escapes
const TERMINAL_COLORS: Record<string, string> = {
  black: '\\c[black]',
  red: '\\c[red]',
  green: '\\c[green]',
  yellow: '\\c[yellow]',
  blue: '\\c[blue]',
  magenta: '\\c[magenta]',
  cyan: '\\c[cyan]',
  white: '\\c[white]',
  brightRed: '\\c[red]',
  brightGreen: '\\c[green]',
  brightYellow: '\\c[yellow]',
  brightBlue: '\\c[blue]',
  brightMagenta: '\\c[magenta]',
  brightCyan: '\\c[cyan]',
  brightWhite: '\\c[white]',
  reset: '\\c[reset]',
};

export class AsciiRenderer {
  private static assetCache: Map<string, string> = new Map();
  private terminalWidth: number;
  private terminalHeight: number;
  private buffer: string[][];
  private terminal: import('../terminal').Terminal | null = null;

  constructor(widthOrTerminal: number | import('../terminal').Terminal = 80, height: number = 24) {
    if (typeof widthOrTerminal === 'number') {
      this.terminalWidth = widthOrTerminal;
      this.terminalHeight = height;
    } else {
      this.terminal = widthOrTerminal;
      const dims = this.terminal.getScreenBuffer().getDimensions();
      this.terminalWidth = dims.width;
      this.terminalHeight = dims.height;
    }

    this.buffer = this.createBuffer();
  }

  /**
   * Render ASCII art from a file path relative to /ascii
   */
  public async renderFromFile(
    filePath: string,
    options: RenderOptions & { clear?: boolean } = {}
  ): Promise<void> {
    const content = await AsciiRenderer.loadAsset(filePath);
    if (!content) {
      return;
    }

    if (this.terminal) {
      if (options.clear) {
        this.terminal.clear();
      }
      const lines = content.split(/\r?\n/);
      this.terminal.printLines(lines);
      return;
    }

    this.renderAscii(content, 0, 0, options);
  }

  private static async loadAsset(filePath: string): Promise<string | null> {
    const normalized = filePath.replace(/^\/+/, '');
    if (AsciiRenderer.assetCache.has(normalized)) {
      return AsciiRenderer.assetCache.get(normalized) || null;
    }

    try {
      const response = await fetch(`/ascii/${normalized}`);
      if (!response.ok) {
        console.warn(`ASCII asset not found: ${normalized}`);
        return null;
      }

      const content = await response.text();
      AsciiRenderer.assetCache.set(normalized, content);
      return content;
    } catch (error) {
      console.warn(`Failed to load ASCII asset: ${normalized}`, error);
      return null;
    }
  }

  /**
   * Create an empty buffer
   */
  private createBuffer(): string[][] {
    return Array(this.terminalHeight)
      .fill(null)
      .map(() => Array(this.terminalWidth).fill(' '));
  }

  /**
   * Clear the buffer
   */
  public clear(): void {
    this.buffer = this.createBuffer();
  }

  /**
   * Set terminal dimensions
   */
  public setDimensions(width: number, height: number): void {
    this.terminalWidth = width;
    this.terminalHeight = height;
    this.buffer = this.createBuffer();
  }

  /**
   * Get current dimensions
   */
  public getDimensions(): { width: number; height: number } {
    return { width: this.terminalWidth, height: this.terminalHeight };
  }

  /**
   * Render ASCII art string to buffer
   */
  public renderAscii(
    art: string,
    startX: number = 0,
    startY: number = 0,
    options: RenderOptions = {}
  ): void {
    const lines = art.split('\n');

    let x = startX;
    let y = startY;

    // Center the art if requested
    if (options.centered) {
      const maxLineLength = Math.max(...lines.map(line => this.getVisibleLength(line)));
      x = Math.floor((this.terminalWidth - maxLineLength) / 2);
      y = Math.floor((this.terminalHeight - lines.length) / 2);
    }

    for (let i = 0; i < lines.length; i++) {
      const lineY = y + i;
      if (lineY < 0 || lineY >= this.terminalHeight) continue;

      const line = lines[i];
      for (let j = 0; j < line.length; j++) {
        const charX = x + j;
        if (charX < 0 || charX >= this.terminalWidth) continue;

        this.buffer[lineY][charX] = line[j];
      }
    }
  }

  /**
   * Get visible length of string (excluding ANSI codes)
   */
  private getVisibleLength(str: string): number {
    // Remove ANSI escape codes for length calculation
    return str.replace(/\x1b\[[0-9;]*m/g, '').length;
  }

  /**
   * Render centered ASCII art
   */
  public renderCentered(art: string, options: RenderOptions = {}): void {
    this.renderAscii(art, 0, 0, { ...options, centered: true });
  }

  /**
   * Draw a box with optional title
   */
  public drawBox(
    x: number,
    y: number,
    options: BoxOptions
  ): void {
    const style = BOX_STYLES[options.style || 'single'];
    const { width, height, title, padding = 0 } = options;

    // Top border
    let topLine = style.topLeft;
    if (title) {
      const titleWithPadding = ` ${title} `;
      const remainingWidth = width - 2 - titleWithPadding.length;
      const leftPart = Math.floor(remainingWidth / 2);
      const rightPart = remainingWidth - leftPart;
      topLine += style.horizontal.repeat(leftPart);
      topLine += titleWithPadding;
      topLine += style.horizontal.repeat(rightPart);
    } else {
      topLine += style.horizontal.repeat(width - 2);
    }
    topLine += style.topRight;

    this.renderLine(topLine, x, y);

    // Side borders
    for (let i = 1; i < height - 1; i++) {
      this.setChar(x, y + i, style.vertical);
      this.setChar(x + width - 1, y + i, style.vertical);
    }

    // Bottom border
    const bottomLine = style.bottomLeft +
      style.horizontal.repeat(width - 2) +
      style.bottomRight;
    this.renderLine(bottomLine, x, y + height - 1);
  }

  /**
   * Render a single line at position
   */
  public renderLine(line: string, x: number, y: number): void {
    if (y < 0 || y >= this.terminalHeight) return;

    for (let i = 0; i < line.length; i++) {
      const charX = x + i;
      if (charX >= 0 && charX < this.terminalWidth) {
        this.buffer[y][charX] = line[i];
      }
    }
  }

  /**
   * Set a single character
   */
  public setChar(x: number, y: number, char: string): void {
    if (x >= 0 && x < this.terminalWidth && y >= 0 && y < this.terminalHeight) {
      this.buffer[y][x] = char;
    }
  }

  /**
   * Get a single character
   */
  public getChar(x: number, y: number): string {
    if (x >= 0 && x < this.terminalWidth && y >= 0 && y < this.terminalHeight) {
      return this.buffer[y][x];
    }
    return ' ';
  }

  /**
   * Apply text overlay on buffer
   */
  public applyOverlay(overlay: TextOverlay): void {
    const { text, x, y, color } = overlay;
    let processedText = text;

    if (color && TERMINAL_COLORS[color]) {
      processedText = TERMINAL_COLORS[color] + text + TERMINAL_COLORS.reset;
    }

    // Use processedText (with color codes) instead of raw text
    this.renderLine(processedText, x, y);
  }

  /**
   * Apply multiple overlays
   */
  public applyOverlays(overlays: TextOverlay[]): void {
    for (const overlay of overlays) {
      this.applyOverlay(overlay);
    }
  }

  /**
   * Render text with color
   */
  public renderColoredText(
    text: string,
    x: number,
    y: number,
    color: string
  ): string {
    const colorCode = TERMINAL_COLORS[color] || '';
    const reset = TERMINAL_COLORS.reset;

    this.renderLine(text, x, y);

    // Return the colored version for direct output
    return `${colorCode}${text}${reset}`;
  }

  /**
   * Get the buffer as a string
   */
  public toString(): string {
    return this.buffer.map(row => row.join('')).join('\n');
  }

  /**
   * Get the buffer as array of lines
   */
  public toLines(): string[] {
    return this.buffer.map(row => row.join(''));
  }

  /**
   * Create a framed box with content
   */
  public createFramedContent(
    content: string[],
    options: BoxOptions
  ): string {
    const style = BOX_STYLES[options.style || 'double'];
    const { width, title, padding = 1 } = options;
    const lines: string[] = [];

    // Top border with title
    let topLine = style.topLeft;
    if (title) {
      const titleWithPadding = ` ${title} `;
      const remainingWidth = width - 2 - titleWithPadding.length;
      const leftPart = Math.floor(remainingWidth / 2);
      const rightPart = remainingWidth - leftPart;
      topLine += style.horizontal.repeat(leftPart);
      topLine += titleWithPadding;
      topLine += style.horizontal.repeat(rightPart);
    } else {
      topLine += style.horizontal.repeat(width - 2);
    }
    topLine += style.topRight;
    lines.push(topLine);

    // Padding lines
    for (let i = 0; i < padding; i++) {
      lines.push(style.vertical + ' '.repeat(width - 2) + style.vertical);
    }

    // Content lines
    const contentWidth = width - 2 - (padding * 2);
    for (const line of content) {
      const paddedLine = line.padEnd(contentWidth).substring(0, contentWidth);
      lines.push(
        style.vertical +
        ' '.repeat(padding) +
        paddedLine +
        ' '.repeat(padding) +
        style.vertical
      );
    }

    // Padding lines
    for (let i = 0; i < padding; i++) {
      lines.push(style.vertical + ' '.repeat(width - 2) + style.vertical);
    }

    // Bottom border
    lines.push(
      style.bottomLeft +
      style.horizontal.repeat(width - 2) +
      style.bottomRight
    );

    return lines.join('\n');
  }

  /**
   * Create horizontal separator
   */
  public createSeparator(
    width: number,
    style: 'single' | 'double' | 'heavy' = 'single'
  ): string {
    const chars = BOX_STYLES[style];
    return chars.horizontal.repeat(width);
  }

  /**
   * Apply ANSI styling to text
   */
  public static styleText(
    text: string,
    options: { color?: string; bold?: boolean; dim?: boolean; blink?: boolean }
  ): string {
    let result = '';

    if (options.bold) result += '\x1b[1m';
    if (options.dim) result += '\x1b[2m';
    if (options.blink) result += '\x1b[5m';
    if (options.color && TERMINAL_COLORS[options.color]) {
      result += TERMINAL_COLORS[options.color];
    }

    result += text;
    result += TERMINAL_COLORS.reset;

    return result;
  }

  /**
   * Get ANSI color code
   */
  public static getColor(color: string): string {
    return TERMINAL_COLORS[color] || '';
  }

  /**
   * Get reset code
   */
  public static getReset(): string {
    return TERMINAL_COLORS.reset;
  }

  /**
   * Merge two ASCII art strings (overlay second on first)
   */
  public static mergeAscii(base: string, overlay: string, x: number, y: number): string {
    const baseLines = base.split('\n');
    const overlayLines = overlay.split('\n');

    for (let i = 0; i < overlayLines.length; i++) {
      const targetY = y + i;
      if (targetY < 0 || targetY >= baseLines.length) continue;

      const baseLine = baseLines[targetY] || '';
      const overlayLine = overlayLines[i];

      let newLine = baseLine.padEnd(x);
      for (let j = 0; j < overlayLine.length; j++) {
        const targetX = x + j;
        if (overlayLine[j] !== ' ') {
          newLine = newLine.substring(0, targetX) +
                   overlayLine[j] +
                   newLine.substring(targetX + 1);
        }
      }
      baseLines[targetY] = newLine;
    }

    return baseLines.join('\n');
  }

  /**
   * Create progress bar
   */
  public static createProgressBar(
    current: number,
    max: number,
    width: number = 40,
    style: 'block' | 'arrow' | 'dots' = 'block'
  ): string {
    const percentage = Math.min(1, Math.max(0, current / max));
    const filled = Math.floor(width * percentage);
    const empty = width - filled;

    let fillChar: string;
    let emptyChar: string;

    switch (style) {
      case 'arrow':
        fillChar = '=';
        emptyChar = '-';
        return '[' + fillChar.repeat(Math.max(0, filled - 1)) +
               (filled > 0 ? '>' : '') +
               emptyChar.repeat(empty) + ']';
      case 'dots':
        fillChar = '●';
        emptyChar = '○';
        return fillChar.repeat(filled) + emptyChar.repeat(empty);
      case 'block':
      default:
        fillChar = '█';
        emptyChar = '░';
        return '[' + fillChar.repeat(filled) + emptyChar.repeat(empty) + ']';
    }
  }
}

export default AsciiRenderer;
